import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import AreteExplainer from './components/AreteExplainer';
import { supabase } from './lib/supabase';
import { useProfile } from './lib/auth';
import { DemoProvider, useDemo } from './lib/demoContext';
import { ThemeProvider } from './lib/themeContext';
import { LabRangeProvider } from './lib/labRangeContext';
import { LensProvider } from './lib/dashboard/lensContext';
import type { Session } from '@supabase/supabase-js';

import ClientPanel from './components/ClientPanel';
import TopNav      from './components/TopNav';
import MobileHeader from './components/dashboard/MobileHeader';
import ErrorBoundary from './components/ErrorBoundary';

// Eagerly loaded (critical path)
import Login       from './pages/Login';
import SetPassword from './pages/SetPassword';
import Dashboard   from './pages/Dashboard';

// Retry wrapper for lazy imports — handles stale chunks after deploy.
// If a chunk fails to load (e.g. hash mismatch after new deploy), reload
// the page once so the browser fetches the updated index.html + chunks.
function lazyRetry<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return lazy(() =>
    factory().catch((err: unknown) => {
      const reloaded = sessionStorage.getItem('chunk_reload');
      if (!reloaded) {
        sessionStorage.setItem('chunk_reload', '1');
        window.location.reload();
        return new Promise(() => {}); // never resolves — page is reloading
      }
      sessionStorage.removeItem('chunk_reload');
      throw err; // genuine error, let ErrorBoundary handle it
    }),
  );
}

// Lazy loaded (non-critical — loaded on navigation)
const Sleep             = lazyRetry(() => import('./pages/Sleep'));
const Activity          = lazyRetry(() => import('./pages/Activity'));
const Nutrition         = lazyRetry(() => import('./pages/Nutrition'));
const Metabolic         = lazyRetry(() => import('./pages/Metabolic'));
const Methodology       = lazyRetry(() => import('./pages/Methodology'));
const StressPage        = lazyRetry(() => import('./pages/Stress'));
const SocialSubstances  = lazyRetry(() => import('./pages/SocialSubstances'));
const Labs              = lazyRetry(() => import('./pages/Labs'));
const CheckIn      = lazyRetry(() => import('./pages/CheckIn'));
const History      = lazyRetry(() => import('./pages/History'));
const Settings     = lazyRetry(() => import('./pages/Settings'));
const Connect      = lazyRetry(() => import('./pages/Connect'));
const Phenotype    = lazyRetry(() => import('./pages/Phenotype'));
const ActionPlanPage = lazyRetry(() => import('./pages/ActionPlan'));
const Admin        = lazyRetry(() => import('./pages/Admin'));
const Patients     = lazyRetry(() => import('./pages/Patients'));
const PatientDetail = lazyRetry(() => import('./pages/PatientDetail'));
const Privacy       = lazyRetry(() => import('./pages/Privacy'));

// Intercept Supabase hash fragments before React mounts (invite / recovery)
try {
  const hash = window.location.hash;
  if (hash.includes('type=recovery') || hash.includes('type=invite')) {
    window.location.replace('/set-password' + hash);
  }
} catch (e) { console.error('[App] hash intercept error:', e); }

/* Fixed forest background + vignette — rendered as elements because
   CRA's webpack can't resolve url() in src/index.css to public/ assets */
/* "Sun Rays Through Canopy" — hope-3.jpg, natural golden light + lush greens */
function ForestBackground() {
  return (
    <>
      {/* Forest image — hope-3 is already vivid, gentle enhancement only */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: -5,
          background: `url('${process.env.PUBLIC_URL}/hope-3.jpg') center/cover no-repeat fixed`,
          filter: 'brightness(1.12) saturate(1.18) contrast(1.04)',
        }}
      />
      {/* Warm color wash — subtle emerald-gold unification */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: -4,
          background: 'linear-gradient(180deg, rgba(130,160,90,0.18) 0%, rgba(110,145,70,0.22) 50%, rgba(90,120,55,0.28) 100%)',
          mixBlendMode: 'color' as React.CSSProperties['mixBlendMode'],
        }}
      />
      {/* Golden sun warmth — complements natural rays in photo */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: -3,
          background: [
            'radial-gradient(ellipse 70% 50% at 50% 18%, rgba(255,240,180,0.15) 0%, transparent 55%)',
            'linear-gradient(180deg, rgba(255,250,230,0.08) 0%, rgba(220,200,130,0.08) 25%, transparent 55%)',
          ].join(', '),
        }}
      />
      {/* Quiet center — calms the text zone for readability */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: -2,
          background: 'radial-gradient(ellipse 55% 50% at 50% 40%, rgba(20,40,20,0.30) 0%, rgba(20,40,20,0.12) 50%, transparent 85%)',
        }}
      />
      {/* Blue sky — soft peek through upper canopy, hopeful accent */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: -1,
          background: [
            // Gentle sky wash — upper area
            'radial-gradient(ellipse 50% 40% at 50% 5%, rgba(130,185,240,0.50) 0%, rgba(110,170,235,0.25) 40%, transparent 72%)',
            // Brighter highlight — where rays break through
            'radial-gradient(ellipse 25% 22% at 55% 8%, rgba(160,210,255,0.55) 0%, rgba(140,195,250,0.25) 45%, transparent 70%)',
            // Wide secondary for natural transition
            'radial-gradient(ellipse 60% 45% at 48% 12%, rgba(145,195,245,0.22) 0%, rgba(125,180,235,0.08) 50%, transparent 80%)',
          ].join(', '),
        }}
      />
    </>
  );
}

function DemoBanner() {
  const demo = useDemo();
  if (!demo) return null;
  return (
    <div className="px-4 py-2 text-center" style={{ background: 'rgba(233,239,228,0.90)', backdropFilter: 'blur(10px)', borderBottom: '1px solid rgba(214,224,207,0.50)' }}>
      <span className="text-xs font-semibold" style={{ color: '#2F4A34' }}>
        Demo Mode — data is simulated
      </span>
      <a href="/login" className="text-xs font-semibold ml-3 hover:underline" style={{ color: '#6F8A5A' }}>
        Sign in →
      </a>
    </div>
  );
}


function AreteBanner() {
  const location = useLocation();
  const [explainerMode, setExplainerMode] = useState<'what' | 'why' | null>(null);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('arete_hero_dismissed') === '1'; }
    catch { return false; }
  });

  // Only show on dashboard
  if (location.pathname !== '/dashboard' && location.pathname !== '/') return null;

  const serif = "'Cormorant Garamond', 'DM Serif Display', Georgia, serif";
  const sans  = "'Inter', -apple-system, BlinkMacSystemFont, sans-serif";

  function handleCollapse() {
    setCollapsed(true);
    try { localStorage.setItem('arete_hero_dismissed', '1'); } catch {}
  }

  function handleExpand() {
    setCollapsed(false);
    try { localStorage.removeItem('arete_hero_dismissed'); } catch {}
  }

  /* ── Compact bar for returning users ── */
  if (collapsed) {
    return (
      <div
        className="flex items-center justify-between transition-all duration-300 ease-in-out"
        style={{
          padding: '18px 2rem',
          position: 'relative',
          background: 'linear-gradient(135deg, rgba(20,38,20,0.35) 0%, rgba(20,38,20,0.18) 100%)',
          backdropFilter: 'blur(6px)',
        }}
      >
        <h2 style={{
          fontFamily: serif,
          fontSize: 'clamp(0.9rem, 2.5vw, 1.25rem)',
          fontWeight: 600,
          color: '#FFFFFF',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          textShadow: '0 1px 12px rgba(0,0,0,0.40), 0 1px 4px rgba(0,0,0,0.25)',
          margin: 0,
        }}>
          The Body Is a <span style={{ color: '#F0EBD8' }}>System,</span> Not a <span style={{ color: '#F0EBD8' }}>Symptom.</span>
        </h2>
        <button
          onClick={handleExpand}
          className="flex items-center justify-center rounded-full transition-colors"
          style={{
            width: 32, height: 32,
            background: 'rgba(198,210,190,0.30)',
            border: '1px solid rgba(214,224,207,0.35)',
            color: '#FFFFFF',
            cursor: 'pointer',
            flexShrink: 0,
          }}
          aria-label="Expand hero section"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M3 5.5L7 9.5L11 5.5" />
          </svg>
        </button>
      </div>
    );
  }

  /* ── Full hero for first-time / expanded ── */
  return (
    <>
      <div
        className="flex flex-col items-center justify-center text-center transition-all duration-300 ease-in-out"
        style={{ minHeight: 380, padding: '4.5rem 2rem 2.5rem', position: 'relative' }}
      >
        {/* Collapse button — top right */}
        <button
          onClick={handleCollapse}
          className="absolute top-4 right-4 flex items-center justify-center rounded-full z-10 transition-colors"
          style={{
            width: 32, height: 32,
            background: 'rgba(198,210,190,0.30)',
            border: '1px solid rgba(214,224,207,0.35)',
            color: '#FFFFFF',
            cursor: 'pointer',
          }}
          aria-label="Collapse hero section"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M3 9L7 5L11 9" />
          </svg>
        </button>
        {/* Quiet center backdrop — calms text zone, edges stay atmospheric */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background: [
              'radial-gradient(ellipse 60% 55% at 50% 42%, rgba(20,38,20,0.32) 0%, rgba(20,38,20,0.14) 50%, transparent 85%)',
              'radial-gradient(ellipse 50% 40% at 50% 42%, rgba(0,0,0,0.10) 0%, transparent 70%)',
            ].join(', '),
          }}
        />

        {/* Headline — centered, uppercase serif, warm bone color */}
        <h1 style={{ lineHeight: 1.12, maxWidth: '46rem', position: 'relative' }}>
          <span style={{
            display: 'block',
            fontFamily: serif,
            fontSize: 'clamp(2rem, 5vw, 3.25rem)',
            fontWeight: 600,
            color: '#FFFFFF',
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            textShadow: '0 2px 30px rgba(0,0,0,0.55), 0 1px 6px rgba(0,0,0,0.35), 0 0px 2px rgba(0,0,0,0.15)',
          }}>
            The Body Is a <span style={{ color: '#F0EBD8' }}>System,</span>
          </span>
          <span style={{
            display: 'block',
            fontFamily: serif,
            fontSize: 'clamp(2rem, 5vw, 3.25rem)',
            fontWeight: 600,
            color: '#FFFFFF',
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            marginTop: 6,
            textShadow: '0 2px 30px rgba(0,0,0,0.55), 0 1px 6px rgba(0,0,0,0.35), 0 0px 2px rgba(0,0,0,0.15)',
          }}>
            Not a <span style={{ color: '#F0EBD8' }}>Symptom.</span>
          </span>
        </h1>

        {/* Subtitle — clearly readable, secondary to headline */}
        <p className="mt-6" style={{
          fontFamily: sans, fontWeight: 400, fontSize: '1.08rem',
          color: '#FFFFFF', lineHeight: 1.7,
          letterSpacing: '0.02em', maxWidth: '30rem',
          textShadow: '0 2px 16px rgba(0,0,0,0.50), 0 1px 4px rgba(0,0,0,0.30)',
          position: 'relative',
        }}>
          Where the pieces of health come together.
        </p>

        {/* CTA Buttons — pill-shaped, oliveSoft background, arete-forest text */}
        <div className="flex flex-col sm:flex-row items-center gap-4 mt-8">
          <button
            onClick={() => setExplainerMode('what')}
            style={{
              fontFamily: sans, fontWeight: 600, fontSize: '0.78rem',
              color: '#2F4A34',
              background: 'rgba(198,210,190,0.92)',
              padding: '12px 32px',
              borderRadius: '9999px',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              border: '1px solid rgba(214,224,207,0.50)',
              cursor: 'pointer',
              backdropFilter: 'blur(12px)',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(214,224,207,0.97)'; e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(47,74,52,0.12)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(198,210,190,0.92)'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            What is Arete?
          </button>
          <button
            onClick={() => setExplainerMode('why')}
            style={{
              fontFamily: sans, fontWeight: 600, fontSize: '0.78rem',
              color: '#2F4A34',
              background: 'rgba(198,210,190,0.88)',
              padding: '12px 32px',
              borderRadius: '9999px',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              border: '1px solid rgba(214,224,207,0.50)',
              cursor: 'pointer',
              backdropFilter: 'blur(12px)',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(214,224,207,0.95)'; e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(47,74,52,0.12)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(198,210,190,0.88)'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            Why Arete?
          </button>
        </div>
      </div>
      {explainerMode && <AreteExplainer mode={explainerMode} onClose={() => setExplainerMode(null)} />}
    </>
  );
}

/**
 * LensProviderWithProfile — wires the dashboard lens to the user's profile
 * role so clinicians and admins default to the clinician lens on their first
 * device load. The LensProvider only consults the hint when localStorage is
 * empty, so an explicit toggle is preserved forever.
 */
function LensProviderWithProfile({ children }: { children: React.ReactNode }) {
  const { isClinician } = useProfile();
  return (
    <LensProvider defaultLensHint={isClinician ? 'clinician' : undefined}>
      {children}
    </LensProvider>
  );
}

function AppShell({ children }: { children: React.ReactNode }) {
  const { isAdmin, isClinician } = useProfile();
  return (
    <div className="flex flex-col min-h-screen">
      <ForestBackground />
      <DemoBanner />
      {/* Mobile web nav (< md) — sticky top header + hamburger drawer.
          Replaces the retired bottom tab bar so the website no longer
          feels like a wrapped native app. */}
      <MobileHeader isAdmin={isAdmin} isClinician={isClinician} />
      {/* Tablet + desktop nav (md+) — horizontal top header.
          PRIMARY_TABS always inline; SECONDARY_TABS fold into a
          "More" dropdown at md/lg, inline at xl+. */}
      <TopNav isAdmin={isAdmin} isClinician={isClinician} />
      <div className="flex flex-1">
        {/* Patient context rail — desktop only (lg+) */}
        <ClientPanel />
        <div className="flex-1 flex flex-col overflow-y-auto">
          <AreteBanner />
          <main className="flex-1">
            <div className="max-w-5xl mx-auto px-4 py-8 lg:px-8">
              <ErrorBoundary inline section="Page content">
              <Suspense fallback={
                <div className="flex justify-center items-center py-24">
                  <div className="w-8 h-8 border-4 border-arete-blue border-t-transparent rounded-full animate-spin" />
                </div>
              }>
                {children}
              </Suspense>
              </ErrorBoundary>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

function RequireAuth({ session, children }: { session: Session | null; children: React.ReactNode }) {
  if (!session) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function RequireAdmin({ session, children }: { session: Session | null; children: React.ReactNode }) {
  const { isAdmin, loading } = useProfile();
  if (!session) return <Navigate to="/login" replace />;
  if (loading) return (
    <div className="flex items-center justify-center py-20" style={{ minHeight: '100vh', background: '#1B3022' }}>
      <div className="w-6 h-6 border-2 border-[rgba(139,184,138,0.6)] border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!isAdmin) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

function DemoActivator({ onActivate }: { onActivate: () => void }) {
  const location = useLocation();
  useEffect(() => {
    if (location.pathname === '/demo') onActivate();
  }, [location.pathname, onActivate]);
  return null;
}

function DemoRoute({ onActivate, demoMode }: { onActivate: () => void; demoMode: boolean }) {
  useEffect(() => { onActivate(); }, [onActivate]);
  if (!demoMode) return (
    <div style={{ minHeight: '100vh', background: '#1B3022', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ width: 32, height: 32, border: '4px solid rgba(139,184,138,0.6)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    </div>
  );
  return <Navigate to="/dashboard" replace />;
}

export default function App() {
  const [session, setSession]   = useState<Session | null>(null);
  const [loading, setLoading]   = useState(true);
  const [demoMode, setDemoMode] = useState(false);

  useEffect(() => {
    // Timeout fallback so spinner never hangs indefinitely
    const timeout = setTimeout(() => setLoading(false), 5000);

    supabase.auth.getSession().then(({ data: { session } }) => {
      clearTimeout(timeout);
      setSession(session);
      setLoading(false);
    }).catch(() => {
      clearTimeout(timeout);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setLoading(false);
    });
    return () => { subscription.unsubscribe(); clearTimeout(timeout); };
  }, []);

  const activateDemo = React.useCallback(() => setDemoMode(true), []);
  const authOrDemo = demoMode || session;

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#1B3022', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{ width: 32, height: 32, border: '4px solid rgba(139,184,138,0.6)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      </div>
    );
  }

  return (
    <ThemeProvider>
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <DemoProvider value={demoMode}>
      <LabRangeProvider>
      <LensProviderWithProfile>
      <ErrorBoundary>
      <DemoActivator onActivate={activateDemo} />
      <Routes>
        {/* Public */}
        <Route path="/login"        element={<Login />} />
        <Route path="/set-password" element={<SetPassword />} />
        <Route path="/privacy"      element={<Suspense fallback={null}><Privacy /></Suspense>} />

        {/* Demo entry — activates demo then redirects to dashboard */}
        <Route path="/demo" element={<DemoRoute onActivate={activateDemo} demoMode={demoMode} />} />

        {/* Protected (or demo) */}
        <Route path="/dashboard" element={
          authOrDemo ? <AppShell><Dashboard /></AppShell> : <Navigate to="/login" replace />
        } />
        <Route path="/sleep" element={
          authOrDemo ? <AppShell><Sleep /></AppShell> : <Navigate to="/login" replace />
        } />
        <Route path="/movement" element={
          authOrDemo ? <AppShell><Activity /></AppShell> : <Navigate to="/login" replace />
        } />
        <Route path="/activity" element={<Navigate to="/movement" replace />} />
        <Route path="/nutrition" element={
          authOrDemo ? <AppShell><Nutrition /></AppShell> : <Navigate to="/login" replace />
        } />
        <Route path="/metabolic" element={
          authOrDemo ? <AppShell><Metabolic /></AppShell> : <Navigate to="/login" replace />
        } />
        <Route path="/methodology" element={
          authOrDemo ? <AppShell><Methodology /></AppShell> : <Navigate to="/login" replace />
        } />
        <Route path="/stress" element={
          authOrDemo ? <AppShell><StressPage /></AppShell> : <Navigate to="/login" replace />
        } />
        <Route path="/social-substances" element={
          authOrDemo ? <AppShell><SocialSubstances /></AppShell> : <Navigate to="/login" replace />
        } />
        <Route path="/labs" element={
          authOrDemo ? <AppShell><Labs /></AppShell> : <Navigate to="/login" replace />
        } />
        <Route path="/checkin" element={
          authOrDemo ? <AppShell><CheckIn /></AppShell> : <Navigate to="/login" replace />
        } />
        <Route path="/history" element={
          authOrDemo ? <AppShell><History /></AppShell> : <Navigate to="/login" replace />
        } />
        <Route path="/settings" element={
          authOrDemo ? <AppShell><Settings /></AppShell> : <Navigate to="/login" replace />
        } />
        <Route path="/phenotype" element={
          authOrDemo ? <AppShell><Phenotype /></AppShell> : <Navigate to="/login" replace />
        } />
        <Route path="/action-plan" element={
          authOrDemo ? <AppShell><ActionPlanPage /></AppShell> : <Navigate to="/login" replace />
        } />
        <Route path="/connect" element={
          authOrDemo ? <AppShell><Connect /></AppShell> : <Navigate to="/login" replace />
        } />
        <Route path="/patients" element={
          authOrDemo ? <AppShell><Patients /></AppShell> : <Navigate to="/login" replace />
        } />
        <Route path="/patients/:patientId" element={
          authOrDemo ? <AppShell><PatientDetail /></AppShell> : <Navigate to="/login" replace />
        } />
        <Route path="/admin" element={
          <RequireAdmin session={session}>
            <AppShell><Admin /></AppShell>
          </RequireAdmin>
        } />

        {/* Default redirect */}
        <Route path="*" element={<Navigate to={authOrDemo ? '/dashboard' : '/login'} replace />} />
      </Routes>
      </ErrorBoundary>
      </LensProviderWithProfile>
      </LabRangeProvider>
      </DemoProvider>
    </BrowserRouter>
    </ThemeProvider>
  );
}
